import { AfterViewChecked, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Constant } from '../constant';
import { MasterItem } from '../model/my-event';
import {
  EventSearchCondition,
  EventSearchConditionType,
  EventSearchOrder,
  EventSearchRequest,
  EventSearchShowChip,
  EventSearchShowCondition,
} from '../model/event-search';

interface GroupConfig {
  masterType: string;
  label: string;
  parentId?: () => number | null;
}

@Component({
  selector: 'app-event-search-filter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCheckboxModule,
    MatSelectModule,
    MatFormFieldModule,
  ],
  templateUrl: './event-search-filter.component.html',
  styleUrl: './event-search-filter.component.css',
})
export class EventSearchFilterComponent implements AfterViewChecked, OnDestroy {
  @Input() masterData: { [key: string]: MasterItem[] } = {};
  @Output() search = new EventEmitter<EventSearchRequest>();

  private readonly searchSubject = new Subject<EventSearchRequest>();
  private readonly searchSub: Subscription;

  public readonly Constant = Constant;

  public tabs: { title: string; types: EventSearchConditionType[] }[] = [
    {
      title: 'カテゴリー',
      types: [Constant.esConditionTypeCategoryMajor, Constant.esConditionTypeCategorySub],
    },
    {
      title: '条件を追加',
      types: [
        Constant.esConditionTypePurpose,
        Constant.esConditionTypeSocialTheme,
        Constant.esConditionTypeImproveState,
        Constant.esConditionTypeImproveOutcome,
      ],
    },
    {
      title: '対象等',
      types: [Constant.esConditionTypeOrganizerUnit, Constant.esConditionTypeParticipantCategory],
    },
    {
      title: 'キーワード',
      types: [Constant.esConditionTypeKeywordTitle, Constant.esConditionTypeKeywordSummary],
    },
    {
      title: '検索範囲',
      types: [Constant.esConditionTypeScopeOrg],
    },
  ];

  public purposeSidenav: { title: string; types: EventSearchConditionType[] }[] = [
    { title: '目的', types: [Constant.esConditionTypePurpose] },
    { title: '社会テーマ', types: [Constant.esConditionTypeSocialTheme] },
    { title: '改善指標', types: [Constant.esConditionTypeImproveState, Constant.esConditionTypeImproveOutcome] },
  ];

  public targetSidenav: { title: string; types: EventSearchConditionType[] }[] = [
    { title: '開催単位', types: [Constant.esConditionTypeOrganizerUnit] },
    { title: '対象者', types: [Constant.esConditionTypeParticipantCategory] },
  ];

  public orderOptions = Constant.esOrderSelect;

  public selectedTabIdx: number | null = null;
  public selectedSidenavIdx = 0;
  public panelLeft: number | null = 16;
  public panelRight: number | null = null;

  @ViewChild('tabNavWrapper') tabNavWrapperRef!: ElementRef<HTMLElement>;

  public condition: EventSearchCondition[] = [];
  public showCondition: EventSearchShowCondition[] = [];

  public selectedCategoryMajorId: number | null = null;
  public scopeOrg: string | null = null;
  public order: EventSearchOrder = Constant.esOrderNewest;

  public keywordTitleInput = '';
  public keywordSummaryInput = '';

  private pendingScrollChipKey: string | null = null;

  constructor(private elementRef: ElementRef) {
    this.searchSub = this.searchSubject
      .pipe(debounceTime(300))
      .subscribe(request => this.search.emit(request));
  }

  ngAfterViewChecked(): void {
    if (!this.pendingScrollChipKey) {
      return;
    }
    const key = this.pendingScrollChipKey;
    const el = this.elementRef.nativeElement.querySelector(`[data-chip-key="${CSS.escape(key)}"]`);
    if (el) {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      this.pendingScrollChipKey = null;
    }
  }

  ngOnDestroy(): void {
    this.searchSub.unsubscribe();
  }

  // 追加したチップが見切れている場合にスクロールして表示する
  private scrollChipIntoView(type: EventSearchConditionType, id: number | string): void {
    this.pendingScrollChipKey = `${type}-${id}`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (document.querySelector('.cdk-overlay-backdrop')) {
      return;
    }
    // 並び替え等、タブ・パネル以外の場所をクリックしたら閉じるようにする
    const wrapperEl = this.tabNavWrapperRef?.nativeElement;
    const clickedInside = !!wrapperEl && event.composedPath().includes(wrapperEl);
    if (this.selectedTabIdx !== null && !clickedInside) {
      this.selectedTabIdx = null;
    }
  }

  // =========================
  // マスタ取得
  // =========================
  getMaster(masterType: string): MasterItem[] {
    return this.masterData?.[masterType] || [];
  }

  getCategorySubMaster(): MasterItem[] {
    if (this.selectedCategoryMajorId == null) {
      return [];
    }
    return this.getMaster(Constant.esMasterTypeCategorySub)
      .filter(m => m.parentId === this.selectedCategoryMajorId);
  }

  // 「全体」は開催単位の選択肢に存在しないため除外する（対象組織の単位でのみ使用）
  getOrganizerUnitMaster(): MasterItem[] {
    return this.getMaster(Constant.esMasterTypeOrganizerUnit)
      .filter(m => m.name !== '全体');
  }

  // =========================
  // タブ / サイドナビ
  // =========================
  onTab(idx: number, event: MouseEvent): void {
    if (this.selectedTabIdx === idx) {
      this.selectedTabIdx = null;
      return;
    }
    this.selectedTabIdx = idx;
    this.selectedSidenavIdx = 0;
    this.updatePanelPosition(event.currentTarget as HTMLElement);
  }

  // クリックしたタブの直下にパネルを表示する（タブごとに横幅が異なるため実測して位置決め）
  private updatePanelPosition(tabEl: HTMLElement): void {
    const wrapperEl = this.tabNavWrapperRef?.nativeElement;
    if (!wrapperEl || !tabEl) {
      return;
    }

    const wrapperWidth = wrapperEl.offsetWidth;
    const tabLeft = tabEl.offsetLeft;
    const tabRight = tabLeft + tabEl.offsetWidth;

    if (tabLeft < wrapperWidth / 2) {
      this.panelLeft = tabLeft;
      this.panelRight = null;
    } else {
      this.panelLeft = null;
      this.panelRight = wrapperWidth - tabRight;
    }
  }

  onSidenav(idx: number): void {
    this.selectedSidenavIdx = idx;
  }

  tabHasSelection(idx: number): boolean {
    const types = this.tabs[idx].types;
    return this.showCondition.some(sc => types.includes(sc.type));
  }

  sidenavHasSelection(types: EventSearchConditionType[]): boolean {
    return this.showCondition.some(sc => types.includes(sc.type));
  }

  // =========================
  // 選択状態の判定
  // =========================
  isSelected(type: EventSearchConditionType, id: number): boolean {
    return this.condition.some(c => c.type === type && c.id === id);
  }

  // =========================
  // カテゴリー大分類
  // =========================
  onSelectCategoryMajor(id: number): void {
    if (this.selectedCategoryMajorId === id) {
      return;
    }
    this.condition = this.condition.filter(c =>
      c.type !== Constant.esConditionTypeCategoryMajor &&
      c.type !== Constant.esConditionTypeCategorySub
    );
    this.condition.push({ type: Constant.esConditionTypeCategoryMajor, id });
    this.selectedCategoryMajorId = id;

    this.rebuildGroup(Constant.esConditionTypeCategoryMajor);
    this.showCondition = this.showCondition.filter(sc => sc.type !== Constant.esConditionTypeCategorySub);
    this.scrollChipIntoView(Constant.esConditionTypeCategoryMajor, id);

    this.emitSearch();
  }

  // =========================
  // チェックボックス（複数選択）共通
  // =========================
  onToggleCondition(type: EventSearchConditionType, id: number, checked: boolean): void {
    if (checked) {
      this.condition.push({ type, id });
    } else {
      this.condition = this.condition.filter(c => !(c.type === type && c.id === id));
    }
    this.rebuildGroup(type);
    if (checked) {
      this.scrollChipIntoView(type, id);
    }
    this.emitSearch();
  }

  // =========================
  // 検索範囲（作成組織）
  // =========================
  onSelectScope(value: string): void {
    this.scopeOrg = value;
    this.condition = this.condition.filter(c => c.type !== Constant.esConditionTypeScopeOrg);
    this.condition.push({ type: Constant.esConditionTypeScopeOrg, id: value });

    const title = value === Constant.esScopeOwn ? '自組織のみ' : '他組織のみ';
    const group: EventSearchShowCondition = {
      type: Constant.esConditionTypeScopeOrg,
      connect: Constant.esConnectOr,
      data: [{ id: value, label: '検索範囲：', title }],
    };
    const existingIdx = this.showCondition.findIndex(sc => sc.type === Constant.esConditionTypeScopeOrg);
    if (existingIdx >= 0) {
      this.showCondition[existingIdx] = group;
    } else {
      this.showCondition.push(group);
    }
    this.scrollChipIntoView(Constant.esConditionTypeScopeOrg, value);

    this.emitSearch();
  }

  // =========================
  // キーワード
  // =========================
  keywordCount(type: EventSearchConditionType): number {
    return this.condition.filter(c => c.type === type).length;
  }

  // 最大件数に達していないか（ブランク／確定前の変換中でも押せるよう、入力内容のチェックは行わない）
  canAddKeyword(type: EventSearchConditionType): boolean {
    return this.keywordCount(type) < Constant.esKeywordMaxCount;
  }

  addKeyword(type: EventSearchConditionType): void {
    if (!this.canAddKeyword(type)) {
      return;
    }

    const isTitle = type === Constant.esConditionTypeKeywordTitle;
    // 半角/全角スペースも有効な検索ワードとして扱うためtrimしない
    const raw = isTitle ? this.keywordTitleInput : this.keywordSummaryInput;

    if (!raw) {
      // ブランクでの追加は何も反応させない（同一ワード追加時と同じ無反応挙動）
      return;
    }

    if (isTitle) {
      this.keywordTitleInput = '';
    } else {
      this.keywordSummaryInput = '';
    }

    if (this.condition.some(c => c.type === type && c.id === raw)) {
      return;
    }

    this.condition.push({ type, id: raw });

    const chip: EventSearchShowChip = { id: raw, label: '', title: raw };
    const existingIdx = this.showCondition.findIndex(sc => sc.type === type);
    if (existingIdx >= 0) {
      this.showCondition[existingIdx].data.push(chip);
    } else {
      const label = isTitle ? 'タイトル：' : '概要：';
      chip.label = label;
      this.showCondition.push({ type, connect: Constant.esConnectOr, data: [chip] });
    }
    this.scrollChipIntoView(type, raw);

    this.emitSearch();
  }

  // =========================
  // チップ削除
  // =========================
  onRemoveChip(groupIndex: number, chipIndex: number): void {
    const group = this.showCondition[groupIndex];
    if (!group) {
      return;
    }
    const chip = group.data[chipIndex];
    const type = group.type;

    this.condition = this.condition.filter(c => !(c.type === type && c.id === chip.id));

    if (type === Constant.esConditionTypeCategoryMajor) {
      this.selectedCategoryMajorId = null;
      this.condition = this.condition.filter(c => c.type !== Constant.esConditionTypeCategorySub);
      this.showCondition = this.showCondition.filter(sc =>
        sc.type !== Constant.esConditionTypeCategorySub &&
        sc.type !== Constant.esConditionTypeCategoryMajor
      );
    } else if (type === Constant.esConditionTypeScopeOrg) {
      this.scopeOrg = null;
      this.showCondition.splice(groupIndex, 1);
    } else if (type === Constant.esConditionTypeKeywordTitle || type === Constant.esConditionTypeKeywordSummary) {
      group.data.splice(chipIndex, 1);
      if (group.data.length === 0) {
        this.showCondition.splice(groupIndex, 1);
      } else if (chipIndex === 0) {
        // 先頭チップにのみ見出し（タイトル：／概要：）を持たせているため、
        // 先頭を削除した場合は新しい先頭チップに見出しを引き継ぐ
        group.data[0].label = type === Constant.esConditionTypeKeywordTitle ? 'タイトル：' : '概要：';
      }
    } else {
      this.rebuildGroup(type);
    }

    this.emitSearch();
  }

  onClearAll(): void {
    this.condition = [];
    this.showCondition = [];
    this.selectedCategoryMajorId = null;
    this.scopeOrg = null;
    this.keywordTitleInput = '';
    this.keywordSummaryInput = '';
    this.emitSearch();
  }

  // =========================
  // ソート
  // =========================
  get selectedOrderLabel(): string {
    return this.orderOptions.find(opt => opt.value === this.order)?.label ?? '';
  }

  onOrderChange(value: EventSearchOrder): void {
    this.order = value;
    this.emitSearch();
  }

  // =========================
  // 内部処理
  // =========================
  private getGroupConfig(type: EventSearchConditionType): GroupConfig {
    switch (type) {
      case Constant.esConditionTypeCategoryMajor:
        return { masterType: Constant.esMasterTypeCategoryMajor, label: 'カテゴリー：' };
      case Constant.esConditionTypeCategorySub:
        return { masterType: Constant.esMasterTypeCategorySub, label: '', parentId: () => this.selectedCategoryMajorId };
      case Constant.esConditionTypePurpose:
        return { masterType: Constant.esMasterTypePurpose, label: '目的：' };
      case Constant.esConditionTypeSocialTheme:
        return { masterType: Constant.esMasterTypeSocialTheme, label: '社会テーマ：' };
      case Constant.esConditionTypeImproveState:
        return { masterType: Constant.esMasterTypeImproveState, label: '状態の変化：' };
      case Constant.esConditionTypeImproveOutcome:
        return { masterType: Constant.esMasterTypeImproveOutcome, label: '企画の成果：' };
      case Constant.esConditionTypeOrganizerUnit:
        return { masterType: Constant.esMasterTypeOrganizerUnit, label: '開催単位：' };
      case Constant.esConditionTypeParticipantCategory:
        return { masterType: Constant.esMasterTypeParticipantCategory, label: '対象者：' };
      default:
        return { masterType: '', label: '' };
    }
  }

  private rebuildGroup(type: EventSearchConditionType): void {
    const config = this.getGroupConfig(type);
    const parentId = config.parentId ? config.parentId() : null;
    const master = this.getMaster(config.masterType)
      .filter(m => !config.parentId || m.parentId === parentId);

    const selectedIds = new Set(this.condition.filter(c => c.type === type).map(c => c.id));

    const data: EventSearchShowChip[] = master
      .filter(m => selectedIds.has(m.id))
      .map((m, idx) => ({ id: m.id, label: idx === 0 ? config.label : '', title: m.name }));

    const existingIdx = this.showCondition.findIndex(sc => sc.type === type);

    if (data.length === 0) {
      if (existingIdx >= 0) {
        this.showCondition.splice(existingIdx, 1);
      }
      return;
    }

    const group: EventSearchShowCondition = { type, connect: Constant.esConnectOr, data };

    if (existingIdx >= 0) {
      this.showCondition[existingIdx] = group;
    } else {
      const insertAt = type === Constant.esConditionTypeCategorySub
        ? this.showCondition.findIndex(sc => sc.type === Constant.esConditionTypeCategoryMajor) + 1
        : this.showCondition.length;
      this.showCondition.splice(Math.max(insertAt, 0), 0, group);
    }
  }

  private emitSearch(): void {
    this.searchSubject.next({
      conditions: [...this.condition],
      order: this.order,
    });
  }
}
